/**
 * Route Utilities (pure functions, zero imports)
 * URL parsing and navigation-mode resolution for pretty spot URLs.
 *
 * Kept dependency-free so unit tests can exercise the routing decision
 * table (tests/router/) without a DOM, Sentry, or app state.
 *
 * URL forms handled:
 *   /{slug}                         → spot mode (canonical, pretty URL)
 *   /?spot={slug}                   → spot mode (query form, normalized to /{slug})
 *   /?region=..&area=..&station=..  → legacy station mode (mapped to a spot)
 *   /                               → default spot
 */

/**
 * Top-level paths that can never be spot slugs.
 * Mirrors the reserved-name assertion in scripts/build.mjs — a slug colliding
 * with any of these would shadow a real file/directory in dist/.
 */
export const RESERVED_PATHS = new Set([
    'admin', 'api', 'assets', 'data', 'icons', 'images',
    'index', 'index.html', 'sw', 'sw.js', 'manifest.json',
    'privacy', 'terms', 'methodology', 'attributions',
    'robots.txt', 'sitemap.xml', 'favicon.svg', 'og-image.png'
]);

/** Valid slug shape: lowercase alphanumerics separated by single hyphens */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Check whether a string is a plausible spot slug
 * (shape only — existence is checked against spots data by the caller)
 * @param {string} value - Candidate slug
 * @returns {boolean}
 */
export function isSlugLike(value) {
    if (typeof value !== 'string' || value.length === 0) return false;
    if (RESERVED_PATHS.has(value)) return false;
    if (value.includes('.')) return false;
    return SLUG_RE.test(value);
}

/**
 * Extract a candidate spot slug from a URL pathname
 * Only single-segment paths qualify (/blacks-beach, not /a/b)
 * @param {string} pathname - URL pathname (e.g., '/blacks-beach')
 * @returns {string|null} Candidate slug or null
 */
export function extractPathSlug(pathname) {
    if (typeof pathname !== 'string') return null;
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length !== 1) return null;
    const candidate = segments[0].toLowerCase();
    return isSlugLike(candidate) ? candidate : null;
}

/**
 * Parse a Location-like object into raw route parts
 * @param {{ pathname: string, search: string }} location - window.location or a fake
 * @returns {{ pathSlug: string|null, spot: string|null, region: string|null,
 *             area: string|null, station: string|null }}
 */
export function parseRouteParts(location) {
    const params = new URLSearchParams(location.search || '');
    return {
        pathSlug: extractPathSlug(location.pathname || '/'),
        spot: params.get('spot'),
        region: params.get('region') || params.get('r'),
        area: params.get('area') || params.get('a'),
        station: params.get('station') || params.get('s')
    };
}

/**
 * Resolve which spot the app should show for a parsed URL.
 *
 * Decision table (first match wins):
 *   1. path slug exists in spots        → { slug, source: 'path' }
 *   2. ?spot= exists in spots           → { slug, source: 'param' }
 *   3. legacy params → resolvedStation → first spot for that station
 *                                       → { slug, source: 'station' }
 *   4. otherwise                        → default spot { slug, source: 'default' }
 *
 * @param {Object} parts - Output of parseRouteParts()
 * @param {Object} lookups
 * @param {(slug: string) => boolean} lookups.hasSpot - Slug exists in spots data
 * @param {(stationId: string) => string|null} lookups.spotForStation - First spot slug for a NOAA station
 * @param {string|null} lookups.resolvedStation - Station ID resolved from legacy params
 *        (after region/area defaulting), or null when no legacy params present
 * @param {string} lookups.defaultSpot - Fallback slug (DEFAULTS.spot)
 * @returns {{ slug: string|null, source: 'path'|'param'|'station'|'default' }}
 */
export function resolveSpotRoute(parts, { hasSpot, spotForStation, resolvedStation, defaultSpot }) {
    if (parts.pathSlug && hasSpot(parts.pathSlug)) {
        return { slug: parts.pathSlug, source: 'path' };
    }

    const spotParam = parts.spot && isSlugLike(parts.spot) ? parts.spot : null;
    if (spotParam && hasSpot(spotParam)) {
        return { slug: spotParam, source: 'param' };
    }

    const hasLegacyParams = Boolean(parts.region || parts.area || parts.station);
    if (hasLegacyParams && resolvedStation) {
        const stationSlug = spotForStation(resolvedStation);
        if (stationSlug) {
            return { slug: stationSlug, source: 'station' };
        }
    }

    if (defaultSpot && hasSpot(defaultSpot)) {
        return { slug: defaultSpot, source: 'default' };
    }

    return { slug: null, source: 'default' };
}
